/**
 * shield_look — the player's shield arcs made visible (report §9 R8).
 *
 * The front / rear shield arcs were numbers on the HUD only: a bolt either sparked the hull or vanished. This is
 * a hull ELLIPSOID (the hull's own box × 1.25) that stays invisible until something strikes it:
 *  - a RING BUFFER of 8 hits (hull-local direction + start time) — each one an expanding ripple ring over the
 *    shell with a bright core flash at the impact point;
 *  - the FRONT arc (nose, −Z) tints cyan and the REAR amber, each scaled by what that arc has left;
 *  - an arc that drops to nothing COLLAPSES — a crackling noise dissolve over the whole shell.
 * The mesh shows only while a ripple or a collapse is alive (a mesh visibility flip, never a light's).
 * `space_look` has no rule here beyond the ring timing below; the harness pins `shieldRipple`.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, uniformArray, vec3, float, int, positionGeometry, normalView, positionViewDirection, abs, dot, pow, exp,
  clamp, normalize, mix, smoothstep, acos, Loop, mx_noise_float, max, step,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** A ripple's life (s), its angular speed (rad/s) and ring width (rad) — the CPU mirror of the shader's timing. */
export const SHIELD_RIPPLE = { life: 1.2, speed: 1.6, width: 0.08 } as const;
/** The ripple ring's strength at angle `ang` from the impact, `age` s after it (the shader's formula). */
export function shieldRipple(ang: number, age: number): number {
  if (age < 0 || age >= SHIELD_RIPPLE.life) return 0;
  const r = age * SHIELD_RIPPLE.speed;
  const d = (ang - r) / SHIELD_RIPPLE.width;
  const fade = (1 - age / SHIELD_RIPPLE.life) ** 2;
  return Math.exp(-d * d) * fade + Math.exp(-(ang * ang) / 0.02) * Math.exp(-age * 8);
}

export const shieldState = { hits: 0, collapses: 0, visible: false };

export class HullShield {
  readonly mesh: THREE.Mesh;
  private readonly hits = Array.from({ length: 8 }, () => new THREE.Vector4(0, 0, -1, -100));
  private readonly uHits: N;
  private readonly uTime = uniform(0);
  private readonly uFront = uniform(1);
  private readonly uRear = uniform(1);
  private readonly uCollapse = uniform(-100);
  private next = 0;
  private clock = 0;
  private lastHit = -100;

  /** `half` — the hull's half extents (m, hull-local, nose −Z); the shell is 1.25× that ellipsoid. */
  constructor(half: THREE.Vector3) {
    this.uHits = uniformArray(this.hits, 'vec4');
    const geo = new THREE.SphereGeometry(1, 48, 32);
    geo.userData.shared = false;
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    const hits = this.uHits, uT = this.uTime, uF = this.uFront, uR = this.uRear, uC = this.uCollapse;
    mat.colorNode = Fn(() => {
      const n = normalize(positionGeometry);                        // MEASURE the unit sphere (hull-local direction)
      const facing = abs(dot(normalView, positionViewDirection));
      const rim = pow(float(1).sub(facing), float(3));
      const rear = smoothstep(-0.2, 0.2, n.z);                       // nose −Z = front
      const tint = mix(vec3(0.35, 0.85, 1.0), vec3(1.0, 0.7, 0.3), rear);
      const arc = mix(uF, uR, rear);
      const acc = float(0).toVar();
      Loop({ start: int(0), end: int(8), type: 'int', condition: '<' }, ({ i }: { i: N }) => {
        const h = hits.element(i);
        const age = uT.sub(h.w);
        const alive = step(float(0), age).mul(float(1).sub(step(float(SHIELD_RIPPLE.life), age)));
        const ang = acos(clamp(dot(n, h.xyz), -1, 1));
        const d = ang.sub(age.mul(SHIELD_RIPPLE.speed)).div(SHIELD_RIPPLE.width);
        const fade = float(1).sub(age.div(SHIELD_RIPPLE.life));
        const ring = exp(d.mul(d).negate()).mul(fade.mul(fade));
        const core = exp(ang.mul(ang).div(-0.02)).mul(exp(age.mul(-8)));
        acc.addAssign(ring.add(core).mul(alive));
      });
      const c = uT.sub(uC);
      const collapsing = step(float(0), c).mul(float(1).sub(step(float(1), c)));
      const crackle = step(mx_noise_float(n.mul(14).add(c.mul(3))).mul(0.5).add(0.5), float(1).sub(c)).mul(float(1).sub(c)).mul(collapsing).mul(2.5);
      const body = rim.mul(0.12).mul(max(arc, float(0.15)));
      return tint.mul(body.add(acc.mul(float(0.5).add(arc)).mul(2.2))).add(vec3(0.6, 0.85, 1.0).mul(crackle));
    })();
    mat.name = 'space-shield';
    markMrtOverlay(mat);
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.name = 'space-shield';
    this.mesh.scale.set(Math.max(1, half.x) * 1.25, Math.max(1, half.y) * 1.25, Math.max(1, half.z) * 1.25);
    this.mesh.renderOrder = 7;
    this.mesh.visible = false;
  }

  /** A strike from hull-local direction `dir` (unit, pointing from the hull centre toward the attacker). */
  hit(dir: THREE.Vector3): void {
    const v = this.hits[this.next];
    v.set(dir.x, dir.y, dir.z, this.clock);
    this.next = (this.next + 1) % this.hits.length;
    this.lastHit = this.clock;
    shieldState.hits++;
  }

  /** An arc just went to zero: the whole shell crackles out. */
  collapse(): void { (this.uCollapse as unknown as { value: number }).value = this.clock; this.lastHit = this.clock; shieldState.collapses++; }

  /** Per frame: arcs 0..1 (what each arc has left). */
  tick(dt: number, front01: number, rear01: number): void {
    this.clock += dt;
    (this.uTime as unknown as { value: number }).value = this.clock;
    (this.uFront as unknown as { value: number }).value = Math.max(0, Math.min(1, front01));
    (this.uRear as unknown as { value: number }).value = Math.max(0, Math.min(1, rear01));
    this.mesh.visible = this.clock - this.lastHit < SHIELD_RIPPLE.life + 0.2;
    shieldState.visible = this.mesh.visible;
  }
}
